
'use client';

import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import { uploadImage } from '@/lib/storage';
import { useAuth } from '@/components/providers/auth-provider';
import Image from 'next/image';
import { Skeleton } from './ui/skeleton';
import { Button } from './ui/button';
import { Edit, Save, X, Loader2, Image as ImageIcon } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { ImageUploader } from './artworks/image-uploader';
import { toCloudflareImage } from '@/lib/utils';

interface EditableWelcomeImageProps {
    imageKey: string;
    title: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export function EditableWelcomeImage({ imageKey, title }: EditableWelcomeImageProps) {
    const { user, userRoles } = useAuth();
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const { toast } = useToast();
    const isAdmin = userRoles.includes('admin');

    useEffect(() => {
        const docRef = doc(db, 'site_content', 'welcome_page');
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists() && docSnap.data()[imageKey]) {
                setImageUrl(docSnap.data()[imageKey]);
            } else {
                setImageUrl(null); // No image set yet
            }
            setLoading(false);
        }, (error) => {
            console.error("Error fetching welcome page content:", error);
            setImageUrl(null);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [imageKey]);
    
    const handleSave = async () => {
        if (!imageFile) {
            setIsEditing(false);
            return;
        };

        setIsSaving(true);
        try {
            const fileExtension = imageFile.name.split('.').pop();
            const fileName = `welcome_page_${imageKey}-${Date.now()}.${fileExtension}`;
            const storageRef = ref(storage, `site_content/${fileName}`);
            await uploadImage(storageRef, imageFile);
            const newImageUrl = await getDownloadURL(storageRef);

            const docRef = doc(db, 'site_content', 'welcome_page');
            await setDoc(docRef, { [imageKey]: newImageUrl }, { merge: true });

            toast({ title: "Image Updated", description: `The image for "${title}" has been saved.` });
            setIsEditing(false);
            setImageFile(null);

        } catch(e) {
            console.error("Failed to save image: ", e);
            toast({ title: "Error", description: "Failed to save the new image.", variant: "destructive" });
        } finally {
            setIsSaving(false);
        }
    }
    
    const handleCancel = () => {
        setIsEditing(false);
        setImageFile(null);
    }
    
    const renderContent = () => {
        if (loading) {
            return <Skeleton className="absolute inset-0" />;
        }

        if (isEditing) {
            return (
                <div className="absolute inset-0 bg-background/90 p-4 flex flex-col items-center justify-center gap-4 z-10">
                     <ImageUploader 
                        value={imageFile ? [imageFile] : []} 
                        onChange={(files) => setImageFile(files[0] || null)}
                        onBlur={() => {}}
                        maxFiles={1}
                        maxSize={MAX_FILE_SIZE}
                    />
                    <div className="flex justify-center gap-2">
                        <Button size="sm" variant="outline" onClick={handleCancel} disabled={isSaving}>
                            <X className="mr-2 h-4 w-4" /> Cancel
                        </Button>
                         <Button size="sm" onClick={handleSave} disabled={!imageFile || isSaving}>
                            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                            Save
                        </Button>
                    </div>
                </div>
            );
        }

        if (imageUrl) {
            return (
                <Image
                    src={toCloudflareImage(imageUrl, 'md')}
                    alt={title}
                    fill
                    className="object-cover"
                    sizes="(max-width: 768px) 100vw, 50vw"
                />
            );
        }
        
        return (
             <div className="h-full bg-muted flex flex-col items-center justify-center text-muted-foreground">
                <ImageIcon className="h-12 w-12 mb-2" />
                <p>No Image Set</p>
            </div>
        );
    }

    return (
        <div className="relative h-full w-full group">
            {renderContent()}
            {isAdmin && !isEditing && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20">
                    <Button variant="secondary" onClick={() => setIsEditing(true)}>
                        <Edit className="mr-2 h-4 w-4" />
                        Change Image
                    </Button>
                </div>
            )}
        </div>
    );
}
